import React, { useState, useEffect, useMemo } from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  Pressable,
  Platform,
  type StyleProp,
  type ViewStyle,
} from 'react-native';

import { ChevronDown, Info } from './../icons';
import { SECTION_IDS } from '../../constants/sectionIds';
import { useFormPermissions } from '../../hooks/useFormPermissions';
import { useOfflineToast } from '../../hooks/useOfflineToast';
import {
  opportunitySubsectionShouldExpand,
  OPPORTUNITY_TEAM_FIELD_KEYS,
} from '../../request/steps/opportunityinformation/opportunitySubsectionExpand';
import { SavedAttachment } from '../../services/attachmentService';
import { teamInformationService } from '../../services/teamInformationService';
import { teamMembersService } from '../../services/teamMembersService';
import { colors, spacing, fontSize, fontWeight, lineHeight } from '../../theme';
import { PartnerContributorWithAccess, TeamMember } from '../../types';
import { FormValidationError, getFieldError } from '../../utils/formValidation';
import {
  buildTeamSubtitleSummary,
  computeTeamFillMetric,
} from '../../utils/teamFillMetrics';
import { getTeamMemberUserIds } from '../../utils/teamMemberUtils';
import {
  FormInfoHelpModal,
  StepForm,
  TeamMemberSelector,
  PartnerContributorSelector,
  FormFieldShowInfoIconsProvider,
  useFormFieldShowInfoIcons,
  formFieldInStack,
} from '../forms';
import { SelectedFile } from '../forms/FileUpload';

/** When role lookups return no rows, still show each chosen id (usually email) in the summary list. */
function mergeResolvedTeamMembers(
  resolved: TeamMember[],
  ids: string[],
  fallbackType: TeamMember['type'],
): TeamMember[] {
  if (ids.length === 0) {
    return [];
  }
  const byLowerEmail = new Map<string, TeamMember>();
  for (const m of resolved) {
    const e = (m.email ?? '').trim().toLowerCase();
    if (e) {
      byLowerEmail.set(e, m);
    }
  }
  const merged: TeamMember[] = [];
  for (const rawId of ids) {
    const trimmed = String(rawId).trim();
    const idNorm = trimmed.toLowerCase();
    if (!idNorm) {
      continue;
    }
    const hit = byLowerEmail.get(idNorm);
    if (hit) {
      merged.push(hit);
    } else {
      merged.push({
        id: trimmed,
        email: trimmed,
        name: '',
        type: fallbackType,
      });
    }
  }
  return merged;
}

const isWeb = Platform.OS === 'web';

const ICON_INFO = 16;

const TEAM_INFO_PANELS = {
  vertivSponsors: {
    title: 'Vertiv sponsors',
    bullets: [
      'Sponsors are Vertiv contacts who own or champion this request.',
      'Add everyone who should be kept in the loop; order can reflect escalation or sign-off path.',
    ],
  },
  vertivContributors: {
    title: 'Vertiv contributors',
    bullets: [
      'Additional Vertiv people who will collaborate on documents or the site visit (not the primary owner).',
    ],
  },
  partnerContributors: {
    title: 'Partner contributors',
    bullets: [
      'External partners (for example, integrator or colo) who need access to this request.',
    ],
  },
  teamNotes: {
    title: 'Team notes',
    bullets: [
      'Internal context the team should see: constraints, who to call, or anything not covered elsewhere.',
      'This is optional. Keep sensitive customer details to what your org allows in the system.',
    ],
  },
} as const;

interface TeamSectionProps {
  title: string;
  memberCount: number;
  onPress: () => void;
  disabled?: boolean;
  style?: StyleProp<ViewStyle>;
  /**
   * `formField` — Figma/FormDropdown style: label above, white bordered 48px trigger, chevron (Project Details create flow).
   * `card` (default) — legacy gray header band with inline label + pill badge.
   */
  layout?: 'card' | 'formField';
  required?: boolean;
  /** Optional (i) next to the label, same family as other form fields. */
  onLabelInfoPress?: () => void;
  /** Accessibility label for the info control (screen readers). */
  labelInfoA11yLabel?: string;
}

const teamSectionTestId = (t: string) =>
  `team-section-${t.toLowerCase().replace(/\s+/g, '-')}`;

const TeamSection: React.FC<TeamSectionProps> = ({
  title,
  memberCount,
  onPress,
  disabled = false,
  style: styleFromParent,
  layout = 'card',
  required = false,
  onLabelInfoPress,
  labelInfoA11yLabel,
}) => {
  const showFieldInfoIcons = useFormFieldShowInfoIcons();
  const slug = title.toLowerCase().replace(/\s+/g, '-');
  const hasSelection = memberCount > 0;
  const triggerSummary = hasSelection
    ? `${memberCount} selected`
    : 'None selected';
  const infoA11y =
    labelInfoA11yLabel ??
    (onLabelInfoPress != null ? `Help: ${title}` : undefined);

  if (layout === 'formField') {
    return (
      <View
        style={[styles.teamSectionFormField, styleFromParent, formFieldInStack]}
        testID={teamSectionTestId(title)}
      >
        <View style={styles.teamSectionFormFieldLabelRow}>
          <View style={styles.teamSectionFormFieldLabelRowInner}>
            <View style={styles.teamSectionFormFieldLabelTextWrap}>
              <Text
                style={styles.teamSectionFormFieldLabel}
                accessibilityRole="text"
              >
                {title}
                {required && (
                  <Text style={styles.teamSectionFormFieldRequired}> *</Text>
                )}
              </Text>
            </View>
            {onLabelInfoPress != null && showFieldInfoIcons ? (
              <Pressable
                onPress={onLabelInfoPress}
                style={({ pressed }) => [
                  styles.teamSectionLabelInfoButton,
                  pressed && styles.teamSectionLabelInfoButtonPressed,
                ]}
                accessibilityLabel={infoA11y}
                accessibilityRole="button"
                hitSlop={8}
              >
                <Info size={ICON_INFO} color={colors.textSecondary} />
              </Pressable>
            ) : null}
          </View>
        </View>
        <View
          testID="dropdown-field-surface"
          style={[
            styles.teamSectionFormFieldTriggerSurface,
            disabled && styles.teamSectionFormFieldTriggerSurfaceDisabled,
          ]}
        >
          <TouchableOpacity
            style={styles.teamSectionFormFieldTriggerInner}
            onPress={disabled ? undefined : onPress}
            disabled={disabled}
            activeOpacity={disabled ? 1 : 0.7}
            accessibilityRole="button"
            accessibilityLabel={`${title} ${triggerSummary}`}
            testID={`team-section-action-${slug}`}
          >
            <View style={styles.teamSectionFormFieldTriggerTextClip}>
              <Text
                numberOfLines={1}
                ellipsizeMode="tail"
                style={[
                  styles.teamSectionFormFieldValueText,
                  hasSelection
                    ? styles.teamSectionFormFieldValueSelected
                    : styles.teamSectionFormFieldValuePlaceholder,
                  disabled && styles.teamSectionFormFieldValueDisabled,
                ]}
              >
                {triggerSummary}
              </Text>
            </View>
            <View
              style={styles.teamSectionFormFieldChevron}
              pointerEvents="box-none"
            >
              <ChevronDown size={12} color={disabled ? '#ccc' : '#666'} />
            </View>
          </TouchableOpacity>
        </View>
      </View>
    );
  }

  return (
    <TouchableOpacity
      testID={teamSectionTestId(title)}
      style={[
        styles.teamSection,
        styleFromParent,
        disabled && styles.teamSectionDisabled,
        formFieldInStack,
      ]}
      onPress={disabled ? undefined : onPress}
      disabled={disabled}
    >
      <View style={styles.teamHeader}>
        <Text
          style={[styles.teamTitle, disabled && styles.teamTitleDisabled]}
          numberOfLines={1}
          ellipsizeMode="tail"
        >
          {title}
          {required && (
            <Text style={styles.teamSectionFormFieldRequired}> *</Text>
          )}
        </Text>
        <View style={styles.teamBadges}>
          {hasSelection ? (
            <View style={styles.teamBadge}>
              <Text style={styles.teamBadgeText}>
                {isWeb ? `${memberCount} selected` : memberCount}
              </Text>
            </View>
          ) : (
            <View style={styles.teamBadgeEmpty}>
              <Text style={styles.teamBadgeEmptyText}>
                {isWeb ? 'None selected' : '0'}
              </Text>
            </View>
          )}
        </View>
        {!disabled && (
          <ChevronDown size={14} color="#666" style={styles.expandIcon} />
        )}
      </View>
    </TouchableOpacity>
  );
};

/** Header subtitles are off product-wide, so the card's one line of guidance renders in the body. */
const TEAM_HELPER_TEXT = 'Add other team members to this request';

export interface OpportunityInformationTeamProps {
  formData: {
    stage?: string;
    useDefaultSponsors?: boolean | string;
    region?: string;
    vertivSponsors?: unknown;
    vertivContributors?: unknown;
    partnerContributors?: unknown;
    dealLinked?: string | boolean;
    autoAddedSponsorEmail?: string;
    requestorEmail?: string;
  };
  updateFormData: (field: string, value: unknown) => void;
  requestId?: string; // Optional for creating new requests
  validationErrors?: FormValidationError[];
  disabled?: boolean;
  attachmentsBySection?: Record<string, SavedAttachment[]>;
  onAttachFiles?: (sectionId: string) => void;
  onDeleteAttachment?: (attachmentId: number) => void;
  isEditing?: boolean;
  selectedFiles?: Record<string, SelectedFile[]>;
  onFilesSelected?: (sectionId: string, files: SelectedFile[]) => void;
  offlineMode?: boolean;
  /**
   * When `requestId` is set: if true (default), hydrate team via `getTeamMemberUserIds` and
   * `updateFormData`. If false (Request Detail + Simple Opportunity), load team from API into picker
   * state only so email-based `editedData` is not overwritten with internal user IDs.
   * @default true
   */
  teamHydrationWritesFormData?: boolean;
}

export const OpportunityInformationTeam: React.FC<
  OpportunityInformationTeamProps
> = ({
  formData,
  updateFormData,
  requestId,
  validationErrors = [],
  disabled = false,
  attachmentsBySection = {},
  onAttachFiles,
  onDeleteAttachment,
  isEditing = false,
  selectedFiles = {},
  onFilesSelected,
  offlineMode = false,
  teamHydrationWritesFormData = true,
}) => {
  const {
    canManageSponsors,
    canManageContributors,
    canManagePartnerContributors,
    canReadSponsors,
    canReadContributors,
    canReadPartnerContributors,
    sponsorAccess,
    contributorAccess,
    partnerContributorAccess,
  } = useFormPermissions(formData.stage, requestId);
  const { isOnline } = useOfflineToast();
  const [fieldHelpModal, setFieldHelpModal] = useState<{
    title: string;
    bullets: string[];
  } | null>(null);

  // Info panels are intentionally staged but not exposed until product signs off.
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const openTeamHelp = (key: keyof typeof TEAM_INFO_PANELS) => {
    let p: (typeof TEAM_INFO_PANELS)[keyof typeof TEAM_INFO_PANELS];
    switch (key) {
      case 'vertivSponsors':
        p = TEAM_INFO_PANELS.vertivSponsors;
        break;
      case 'vertivContributors':
        p = TEAM_INFO_PANELS.vertivContributors;
        break;
      case 'partnerContributors':
        p = TEAM_INFO_PANELS.partnerContributors;
        break;
      case 'teamNotes':
        p = TEAM_INFO_PANELS.teamNotes;
        break;
      default: {
        const _exhaustive: never = key;
        return _exhaustive;
      }
    }
    setFieldHelpModal({ title: p.title, bullets: [...p.bullets] });
  };

  // Check if request has manual Vertiv Sponsors (for conditional checkbox logic)
  // Check both database (for saved sponsors) and current state (for unsaved sponsors)

  const [activeModal, setActiveModal] = useState<
    'vertiv_sponsor' | 'vertiv_contributor' | 'partner_contributor' | null
  >(null);

  // Simplified state - no more complex database/legacy state management

  // Simple state for UI display
  const [selectedSponsorsCount, setSelectedSponsorsCount] = useState(0);
  const [selectedContributorsCount, setSelectedContributorsCount] = useState(0);
  const [selectedPartnersCount, setSelectedPartnersCount] = useState(0);
  const [selectedPartners, setSelectedPartners] = useState<
    PartnerContributorWithAccess[]
  >([]);

  // State for displaying full member details
  const [selectedSponsors, setSelectedSponsors] = useState<TeamMember[]>([]);
  const [useDefaultSponsors, setUseDefaultSponsors] = useState(
    formData.useDefaultSponsors === true ||
      formData.useDefaultSponsors === 'true',
  );
  const [defaultSponsors, setDefaultSponsors] = useState<TeamMember[]>([]);

  useEffect(() => {
    setUseDefaultSponsors(
      formData.useDefaultSponsors === true ||
        formData.useDefaultSponsors === 'true',
    );
  }, [formData.useDefaultSponsors]);
  const [selectedContributors, setSelectedContributors] = useState<
    TeamMember[]
  >([]);

  // Memoize region value to prevent infinite loops from formData object recreation
  // Only re-memoize when the actual region string value changes
  const currentRegion = React.useMemo(() => {
    return formData.region?.toLowerCase();
  }, [formData.region]);

  // Normalize team arrays — the API may return JSON strings instead of arrays.
  // Matches ViewRequestOpportunityStep's sponsorSelectedIds/contributorSelectedIds memos.
  const normalizeTeamIds = (value: unknown): string[] => {
    if (Array.isArray(value)) return value.map(String);
    if (typeof value === 'string') {
      try {
        const parsed: unknown = JSON.parse(value);
        return Array.isArray(parsed) ? parsed.map(String) : [];
      } catch {
        return [];
      }
    }
    return [];
  };
  const sponsorSelectedIds = React.useMemo(
    () => normalizeTeamIds(formData.vertivSponsors),
    [formData.vertivSponsors],
  );
  const contributorSelectedIds = React.useMemo(
    () => normalizeTeamIds(formData.vertivContributors),
    [formData.vertivContributors],
  );

  const sponsorsForDisplay = React.useMemo(
    () =>
      mergeResolvedTeamMembers(
        selectedSponsors,
        sponsorSelectedIds,
        'vertiv_sponsor',
      ),
    [selectedSponsors, sponsorSelectedIds],
  );
  const contributorsForDisplay = React.useMemo(
    () =>
      mergeResolvedTeamMembers(
        selectedContributors,
        contributorSelectedIds,
        'vertiv_contributor',
      ),
    [selectedContributors, contributorSelectedIds],
  );

  // Hydrate team state from server on mount / when requestId or online status changes.
  // Intentionally excludes formData fields from deps — user selections update local state
  // directly via handlers; re-running this on every formData change would overwrite unsaved
  // edits with stale server data.
  useEffect(() => {
    let cancelled = false;

    const updateIfCurrent = (callback: () => void) => {
      if (!cancelled) {
        callback();
      }
    };

    if (!isOnline) {
      setSelectedSponsorsCount(0);
      setSelectedContributorsCount(0);
      setSelectedPartnersCount(0);
      setSelectedPartners([]);
      setSelectedSponsors([]);
      setSelectedContributors([]);
      return;
    }

    if (!requestId) {
      return;
    }

    const hydrateFromServer = async () => {
      if (!teamHydrationWritesFormData) {
        try {
          const teamMembers = await teamInformationService.getTeamMembers(
            requestId,
          );
          const organized =
            teamInformationService.organizeTeamMembersByRole(teamMembers);
          if (cancelled) {
            return;
          }
          const sponsorEmails = organized.vertivSponsors.map(m => m.userEmail);
          const contributorEmails = organized.vertivContributors.map(
            m => m.userEmail,
          );
          const partnersWithAccess: PartnerContributorWithAccess[] =
            organized.partnerContributors.map(member => ({
              email: member.userEmail,
              accessLevel: (member.accessLevel || 'read') as
                | 'read'
                | 'read_write',
              name: member.userFullName,
            }));

          // Sync formData so modals (which read selectedIds from formData) show
          // correct selections instead of stale legacy IDs from the request object.
          updateIfCurrent(() => {
            updateFormData('vertivSponsors', sponsorEmails);
            updateFormData('vertivContributors', contributorEmails);
            updateFormData('partnerContributors', partnersWithAccess);
            setSelectedSponsorsCount(sponsorEmails.length);
            setSelectedContributorsCount(contributorEmails.length);
            setSelectedPartnersCount(partnersWithAccess.length);
            setSelectedPartners(partnersWithAccess);
          });

          if (sponsorEmails.length > 0) {
            const members = await teamMembersService.getTeamMembersByEmails(
              sponsorEmails,
            );
            updateIfCurrent(() => setSelectedSponsors(members));
          } else {
            updateIfCurrent(() => setSelectedSponsors([]));
          }

          if (contributorEmails.length > 0) {
            const members = await teamMembersService.getTeamMembersByEmails(
              contributorEmails,
            );
            updateIfCurrent(() => setSelectedContributors(members));
          } else {
            updateIfCurrent(() => setSelectedContributors([]));
          }
        } catch (error) {
          console.error('Failed to load team members for detail view:', error);
        }
        return;
      }

      try {
        const [sponsorIds, contributorIds, partnerIds] = await Promise.all([
          getTeamMemberUserIds(requestId, 'vertiv_sponsor'),
          getTeamMemberUserIds(requestId, 'vertiv_contributor'),
          getTeamMemberUserIds(requestId, 'partner_contributor'),
        ]);
        if (cancelled) {
          return;
        }

        updateIfCurrent(() => {
          updateFormData('vertivSponsors', sponsorIds);
          updateFormData('vertivContributors', contributorIds);
          updateFormData('partnerContributors', partnerIds);
          setSelectedSponsorsCount(sponsorIds.length);
          setSelectedContributorsCount(contributorIds.length);
          setSelectedPartnersCount(partnerIds.length);
        });

        if (sponsorIds.length > 0) {
          const members = await teamMembersService.getTeamMembersByIds(
            sponsorIds,
          );
          updateIfCurrent(() => setSelectedSponsors(members));
        } else {
          updateIfCurrent(() => setSelectedSponsors([]));
        }

        if (contributorIds.length > 0) {
          const members = await teamMembersService.getTeamMembersByIds(
            contributorIds,
          );
          updateIfCurrent(() => setSelectedContributors(members));
        } else {
          updateIfCurrent(() => setSelectedContributors([]));
        }
      } catch (error) {
        console.error('Failed to load team member counts:', error);
      }
    };

    hydrateFromServer();
    return () => {
      cancelled = true;
    };
  }, [requestId, isOnline, teamHydrationWritesFormData, updateFormData]);

  // For new requests (no requestId), sync display state from formData so that
  // selections made via handlers are reflected in badges and member lists.
  useEffect(() => {
    if (requestId || !isOnline) {
      return;
    }

    const syncNewRequestDisplay = async () => {
      const sponsorEmails = sponsorSelectedIds;
      const contributorEmails = contributorSelectedIds;

      setSelectedSponsorsCount(sponsorEmails.length);
      setSelectedContributorsCount(contributorEmails.length);

      if (sponsorEmails.length > 0) {
        try {
          const members = await teamMembersService.getTeamMembersByEmails(
            sponsorEmails,
          );
          setSelectedSponsors(members);
        } catch (error) {
          console.error('Failed to fetch sponsor details:', error);
        }
      } else {
        setSelectedSponsors([]);
      }

      if (contributorEmails.length > 0) {
        try {
          const members = await teamMembersService.getTeamMembersByEmails(
            contributorEmails,
          );
          setSelectedContributors(members);
        } catch (error) {
          console.error('Failed to fetch contributor details:', error);
        }
      } else {
        setSelectedContributors([]);
      }

      const partnerContributors = Array.isArray(formData.partnerContributors)
        ? formData.partnerContributors
        : [];
      if (partnerContributors.length > 0) {
        const firstPartner = partnerContributors[0];
        const isNewFormat =
          typeof firstPartner === 'object' &&
          firstPartner !== null &&
          'accessLevel' in firstPartner;

        if (isNewFormat) {
          setSelectedPartners(
            partnerContributors as PartnerContributorWithAccess[],
          );
          setSelectedPartnersCount(partnerContributors.length);
        } else {
          const legacyPartners = partnerContributors.map(String);
          const converted = legacyPartners.map((email: string) => ({
            email,
            accessLevel: 'read' as const,
          }));
          setSelectedPartners(converted);
          setSelectedPartnersCount(legacyPartners.length);
        }
      } else {
        setSelectedPartners([]);
        setSelectedPartnersCount(0);
      }
    };

    syncNewRequestDisplay();
  }, [
    requestId,
    sponsorSelectedIds,
    contributorSelectedIds,
    formData.partnerContributors,
    isOnline,
  ]);

  // Handle region changes when "I don't know my Sponsor" is checked
  // Uses memoized currentRegion to prevent infinite loops from formData object recreation
  useEffect(() => {
    if (useDefaultSponsors && currentRegion) {
      const loadDefaultSponsorsForRegion = async () => {
        try {
          const sponsors = await teamMembersService.getDefaultSponsorsByRegion(
            currentRegion,
          );

          if (sponsors.length === 0) {
            console.warn(
              `No Default Sponsors configured for region: ${currentRegion}`,
            );
          }

          setDefaultSponsors(sponsors);
          setSelectedSponsors(sponsors);
          setSelectedSponsorsCount(sponsors.length);

          const emails = sponsors.map(s => s.email);
          updateFormData('vertivSponsors', emails);
        } catch (error) {
          console.error(
            'Error loading Default Sponsors for region change:',
            error,
          );
          setDefaultSponsors([]);
          setSelectedSponsors([]);
          setSelectedSponsorsCount(0);
        }
      };

      loadDefaultSponsorsForRegion();
    } else if (!useDefaultSponsors && defaultSponsors.length > 0) {
      // Clear Default Sponsors when checkbox is unchecked
      setDefaultSponsors([]);
    }
  }, [
    currentRegion,
    useDefaultSponsors,
    updateFormData,
    defaultSponsors.length,
  ]);

  const handleSponsorSelect = async (ids: string[]) => {
    // Update form data for UI responsiveness - will be saved when form is saved
    updateFormData('vertivSponsors', ids);
    setSelectedSponsorsCount(ids.length);

    // Fetch full member details for display
    if (ids.length > 0 && isOnline) {
      try {
        const members = await teamMembersService.getTeamMembersByEmails(ids);
        setSelectedSponsors(members);
      } catch (error) {
        console.error('Failed to fetch sponsor details:', error);
      }
    } else {
      setSelectedSponsors([]);
    }
  };

  const handleContributorSelect = async (ids: string[]) => {
    // Update form data for UI responsiveness - will be saved when form is saved
    updateFormData('vertivContributors', ids);
    setSelectedContributorsCount(ids.length);

    // Fetch full member details for display
    if (ids.length > 0 && isOnline) {
      try {
        const members = await teamMembersService.getTeamMembersByEmails(ids);
        setSelectedContributors(members);
      } catch (error) {
        console.error('Failed to fetch contributor details:', error);
      }
    } else {
      setSelectedContributors([]);
    }
  };

  const handlePartnerSelect = (partners: PartnerContributorWithAccess[]) => {
    // Update form data for UI responsiveness - will be saved when form is saved
    updateFormData('partnerContributors', partners);
    setSelectedPartners(partners);
    setSelectedPartnersCount(partners.length);
  };

  const showTeamReadOnlySponsorList =
    (canReadSponsors || canManageSponsors) && sponsorsForDisplay.length > 0;
  const showTeamReadOnlyContributorList =
    (canReadContributors || canManageContributors) &&
    contributorsForDisplay.length > 0;
  const showTeamReadOnlyPartnerList =
    canReadPartnerContributors && selectedPartners.length > 0;
  const showSelectedMembersReadOnlySection =
    showTeamReadOnlySponsorList ||
    showTeamReadOnlyContributorList ||
    showTeamReadOnlyPartnerList;

  const teamRoleFillMetric = useMemo(
    () =>
      computeTeamFillMetric({
        isOnline,
        roles: [
          {
            canManage: canManageSponsors,
            access: sponsorAccess,
            count: selectedSponsorsCount,
          },
          {
            canManage: canManageContributors,
            access: contributorAccess,
            count: selectedContributorsCount,
          },
          {
            canManage: canManagePartnerContributors,
            access: partnerContributorAccess,
            count: selectedPartnersCount,
          },
        ],
      }),
    [
      isOnline,
      canManageSponsors,
      sponsorAccess,
      selectedSponsorsCount,
      canManageContributors,
      contributorAccess,
      selectedContributorsCount,
      canManagePartnerContributors,
      partnerContributorAccess,
      selectedPartnersCount,
    ],
  );

  const teamSubtitleSummary = useMemo(
    () =>
      buildTeamSubtitleSummary({
        isOnline,
        roles: [
          { label: 'Sponsors', count: selectedSponsorsCount },
          { label: 'Contributors', count: selectedContributorsCount },
          { label: 'Partners', count: selectedPartnersCount },
        ],
      }),
    [
      isOnline,
      selectedSponsorsCount,
      selectedContributorsCount,
      selectedPartnersCount,
    ],
  );

  const expandTeam = opportunitySubsectionShouldExpand(
    OPPORTUNITY_TEAM_FIELD_KEYS,
    validationErrors,
  );

  const teamInformationSection = (
    <StepForm
      title="Team Information"
      titleSubtitle="Select team members for each role. Click on a section to add or remove members."
      titleSubtitleSummary={teamSubtitleSummary}
      fieldFillCount={teamRoleFillMetric.filled}
      fieldTotalCount={teamRoleFillMetric.total}
      expandSection={expandTeam}
      sectionId={SECTION_IDS.OPPORTUNITY_TEAM}
      requestId={requestId}
      allowAttachments={true}
      attachments={attachmentsBySection[SECTION_IDS.OPPORTUNITY_TEAM] || []}
      onAttachFiles={() => onAttachFiles?.(SECTION_IDS.OPPORTUNITY_TEAM)}
      onDeleteAttachment={onDeleteAttachment}
      isEditing={isEditing || !disabled}
      disabled={disabled}
      selectedFiles={selectedFiles[SECTION_IDS.OPPORTUNITY_TEAM] || []}
      onFilesSelected={
        onFilesSelected
          ? files => onFilesSelected(SECTION_IDS.OPPORTUNITY_TEAM, files)
          : undefined
      }
      isOffline={offlineMode}
      leading={
        <>
          <Text style={styles.teamHelperText}>{TEAM_HELPER_TEXT}</Text>
          {!isOnline ? (
            <Text
              style={styles.subsectionDescription}
              accessibilityLiveRegion="polite"
            >
              <Text style={styles.offlineWarning}>
                Team information is disabled while offline.
              </Text>
            </Text>
          ) : null}
          {getFieldError(validationErrors, 'vertivSponsors') ? (
            <Text
              style={styles.inlineValidationError}
              accessibilityLiveRegion="polite"
            >
              {getFieldError(validationErrors, 'vertivSponsors')}
            </Text>
          ) : null}
        </>
      }
    >
      <>
        {canManageSponsors && (!sponsorAccess || !sponsorAccess.isHidden) && (
          <TeamSection
            title="Vertiv Sponsors"
            memberCount={isOnline ? selectedSponsorsCount : 0}
            onPress={
              isOnline ? () => setActiveModal('vertiv_sponsor') : () => {}
            }
            disabled={disabled || !isOnline}
          />
        )}

        {canManageContributors &&
          (!contributorAccess || !contributorAccess.isHidden) && (
            <TeamSection
              title="Vertiv Contributors"
              memberCount={isOnline ? selectedContributorsCount : 0}
              onPress={
                isOnline ? () => setActiveModal('vertiv_contributor') : () => {}
              }
              disabled={disabled || !isOnline}
            />
          )}

        {canManagePartnerContributors &&
          (!partnerContributorAccess || !partnerContributorAccess.isHidden) && (
            <TeamSection
              title="Partner Contributors"
              memberCount={isOnline ? selectedPartnersCount : 0}
              onPress={
                isOnline
                  ? () => setActiveModal('partner_contributor')
                  : () => {}
              }
              disabled={disabled || !isOnline}
            />
          )}
      </>

      {showSelectedMembersReadOnlySection && (
        <View style={styles.selectedMembersContainer}>
          {showTeamReadOnlySponsorList && (
            <View style={styles.memberList}>
              <Text style={styles.memberListTitle}>Vertiv Sponsors:</Text>
              {useDefaultSponsors && (
                <Text style={styles.defaultSponsorsMessage}>
                  Default Sponsors Assigned
                </Text>
              )}
              {[...sponsorsForDisplay]
                .sort((a, b) => {
                  const nameA = (a.name || a.email || '').toLowerCase();
                  const nameB = (b.name || b.email || '').toLowerCase();
                  return nameA.localeCompare(nameB);
                })
                .map(member => {
                  const hasName = Boolean(
                    member.name && member.name !== member.email,
                  );
                  return (
                    <View
                      key={`sponsor-${member.id || member.email}`}
                      style={styles.memberListItem}
                    >
                      {hasName ? (
                        <Text style={styles.memberListName}>{member.name}</Text>
                      ) : null}
                      <Text style={styles.memberListEmail}>
                        {(member.email || '').toLowerCase()}
                      </Text>
                    </View>
                  );
                })}
            </View>
          )}

          {showTeamReadOnlyContributorList && (
            <View style={styles.memberList}>
              <Text style={styles.memberListTitle}>Vertiv Contributors:</Text>
              {[...contributorsForDisplay]
                .sort((a, b) => {
                  const nameA = (a.name || a.email || '').toLowerCase();
                  const nameB = (b.name || b.email || '').toLowerCase();
                  return nameA.localeCompare(nameB);
                })
                .map(member => {
                  const hasName = Boolean(
                    member.name && member.name !== member.email,
                  );
                  return (
                    <View
                      key={`contributor-${member.id || member.email}`}
                      style={styles.memberListItem}
                    >
                      {hasName ? (
                        <Text style={styles.memberListName}>{member.name}</Text>
                      ) : null}
                      <Text style={styles.memberListEmail}>
                        {(member.email || '').toLowerCase()}
                      </Text>
                    </View>
                  );
                })}
            </View>
          )}

          {showTeamReadOnlyPartnerList && (
            <View style={styles.memberList}>
              <Text style={styles.memberListTitle}>Partner Contributors:</Text>
              {[...selectedPartners]
                .sort((a, b) =>
                  (a.email || '')
                    .toLowerCase()
                    .localeCompare((b.email || '').toLowerCase()),
                )
                .map(partner => (
                  <View
                    key={`partner-${partner.email}`}
                    style={styles.memberListItem}
                  >
                    <View style={styles.memberListInfo}>
                      <Text style={styles.memberListName}>
                        {(partner.email || '').toLowerCase()}
                      </Text>
                      {Platform.OS === 'web' && (
                        <View style={styles.accessLevelBadge}>
                          <Text style={styles.accessLevelBadgeText}>
                            {partner.accessLevel === 'read'
                              ? 'Read Only'
                              : 'Read & Write'}
                          </Text>
                        </View>
                      )}
                    </View>
                    {Platform.OS !== 'web' && (
                      <Text style={styles.memberListEmail}>
                        {partner.accessLevel === 'read'
                          ? 'Read Only'
                          : 'Read & Write'}
                      </Text>
                    )}
                  </View>
                ))}
            </View>
          )}
        </View>
      )}
    </StepForm>
  );

  const teamSelectorModals = (
    <>
      <FormInfoHelpModal
        visible={fieldHelpModal != null}
        onClose={() => setFieldHelpModal(null)}
        title={fieldHelpModal != null ? fieldHelpModal.title : ''}
        bullets={fieldHelpModal != null ? fieldHelpModal.bullets : []}
      />
      {canManageSponsors && isOnline && (
        <TeamMemberSelector
          visible={activeModal === 'vertiv_sponsor'}
          onClose={() => setActiveModal(null)}
          onSelect={handleSponsorSelect}
          selectedIds={sponsorSelectedIds}
          type="vertiv_sponsor"
          title="Vertiv Sponsors"
          linkedEmail={
            formData.dealLinked === 'true'
              ? formData.autoAddedSponsorEmail
              : undefined
          }
          currentRegion={currentRegion}
          allowDefaultSponsorMode={
            !(formData.dealLinked === 'true' && formData.autoAddedSponsorEmail)
          }
          useDefaultSponsors={useDefaultSponsors}
          onDefaultSponsorToggle={useDefault => {
            setUseDefaultSponsors(useDefault);
            updateFormData('useDefaultSponsors', useDefault);
            if (!useDefault) {
              setSelectedSponsors([]);
              updateFormData('vertivSponsors', []);
            }
          }}
        />
      )}

      {canManageContributors && isOnline && (
        <TeamMemberSelector
          visible={activeModal === 'vertiv_contributor'}
          onClose={() => setActiveModal(null)}
          onSelect={handleContributorSelect}
          selectedIds={contributorSelectedIds}
          type="vertiv_contributor"
          title="Vertiv Contributors"
        />
      )}

      {canManagePartnerContributors && isOnline && (
        <PartnerContributorSelector
          visible={activeModal === 'partner_contributor'}
          onClose={() => setActiveModal(null)}
          onSelect={handlePartnerSelect}
          selectedIds={selectedPartners}
          title="Partner Contributors"
          requestorEmail={formData.requestorEmail}
        />
      )}
    </>
  );

  return (
    <View style={styles.container}>
      {/*
        No second "Opportunity" page title: `ProjectInformation` already renders it once
        above this block.
      */}
      <FormFieldShowInfoIconsProvider showIcons={false}>
        {teamInformationSection}
      </FormFieldShowInfoIconsProvider>
      {teamSelectorModals}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  /** `layout="formField"`: same visual system as `FormDropdown` (label + bordered trigger). */
  teamSectionFormField: {
    width: '100%' as const,
    marginBottom: 0,
  },
  /**
   * Label row only — do not force a 48px band: that + `flex-end` pushed one-line labels to the
   * bottom of the band, leaving a large empty gap *above* “Vertiv Sponsors” vs. Project “Region”
   * (normal `FormInput` / `FormDropdown` label height).
   */
  /** Gap below legend → control: on `View` (RN Web ignores bottom margin on `Text` too often). */
  teamSectionFormFieldLabelRow: {
    width: '100%' as const,
    marginBottom: spacing.fieldVerticalGap,
  },
  teamSectionFormFieldLabelRowInner: {
    width: '100%' as const,
    flexDirection: 'row' as const,
    alignItems: 'center' as const,
    justifyContent: 'flex-start' as const,
    flexWrap: 'wrap' as const,
    gap: 4,
  },
  /** Sits with the (i) as one left-aligned cluster — do not `flex: 1` or the icon is pushed to the column edge. */
  teamSectionFormFieldLabelTextWrap: {
    flexGrow: 0,
    flexShrink: 1,
    minWidth: 0,
    maxWidth: '100%' as const,
  },
  teamSectionLabelInfoButton: {
    marginTop: 0,
    padding: spacing.infoIconPadding,
  },
  teamSectionLabelInfoButtonPressed: {
    opacity: 0.65,
  },
  teamSectionFormFieldLabel: {
    fontSize: fontSize.formLabel,
    fontWeight: fontWeight.medium,
    color: '#333',
  },
  teamSectionFormFieldRequired: {
    color: colors.error,
  },
  /**
   * Bordered shell — matches {@link FormDropdown} `dropdown` / `dropdown-field-surface`. Stroke uses
   * `inputBorderNeutral`, not legacy `#ddd` on the inner `<button>` (RN Web).
   */
  teamSectionFormFieldTriggerSurface: {
    width: '100%' as const,
    flexDirection: 'row' as const,
    alignItems: 'center' as const,
    borderWidth: 1,
    borderColor: colors.inputBorderNeutral,
    borderRadius: spacing.formControlBorderRadius,
    paddingHorizontal: spacing.formControlPaddingHorizontal,
    backgroundColor: colors.backgroundPrimary,
    minHeight: spacing.formControlHeight,
    maxHeight: spacing.formControlHeight,
    ...Platform.select({
      web: {
        outlineWidth: 0,
        outlineStyle: 'none' as const,
        cursor: 'pointer' as const,
        backgroundColor: colors.backgroundForm,
        borderColor: colors.inputBorderNeutral,
        ':focus-within': {
          outlineWidth: 0,
          outlineStyle: 'none' as const,
          borderWidth: 2,
          borderColor: colors.inputBorderInteractive,
          boxShadow: 'none' as const,
        },
        ':hover': {
          borderColor: colors.inputBorderInteractive,
        },
      },
      default: {},
    }),
  } as import('react-native').ViewStyle,
  teamSectionFormFieldTriggerSurfaceDisabled: {
    backgroundColor: colors.backgroundFormDisabled,
    borderColor: colors.inputBorderDisabled,
    opacity: 1,
    ...Platform.select({
      web: {
        cursor: 'not-allowed' as const,
        backgroundColor: colors.backgroundFormDisabled,
        borderColor: colors.inputBorderDisabled,
        ':hover': {
          borderColor: colors.inputBorderDisabled,
        },
        ':focus-within': {
          borderWidth: 1,
          borderColor: colors.inputBorderDisabled,
          boxShadow: 'none' as const,
        },
      },
      default: {},
    }),
  } as import('react-native').ViewStyle,
  /** Inner row — same idea as `FormDropdown` `webTriggerRow`: no UA/RN Web button border (#ddd). */
  teamSectionFormFieldTriggerInner: {
    flex: 1,
    minWidth: 0,
    flexDirection: 'row' as const,
    alignItems: 'center' as const,
    justifyContent: 'space-between' as const,
    alignSelf: 'stretch' as const,
    ...Platform.select({
      web: {
        gap: spacing.sm,
        cursor: 'pointer' as const,
        userSelect: 'none' as const,
        borderStyle: 'solid' as const,
        borderWidth: 0,
        borderColor: 'transparent',
        /** Shorthand wins over per-side UA `button` borders in some browsers. */
        border: 'none',
        backgroundColor: 'transparent',
        appearance: 'none' as const,
        WebkitAppearance: 'none' as const,
        outlineWidth: 0,
        outlineStyle: 'none' as const,
        boxShadow: 'none' as const,
      },
      default: {},
    }),
  } as import('react-native').ViewStyle,
  teamSectionFormFieldTriggerTextClip: {
    flex: 1,
    minWidth: 0,
  },
  teamSectionFormFieldValueText: {
    fontSize: fontSize.formLabel,
  },
  teamSectionFormFieldValueSelected: {
    color: '#333',
  },
  teamSectionFormFieldValuePlaceholder: {
    color: colors.inputPlaceholder,
  },
  teamSectionFormFieldValueDisabled: {
    color: colors.textFormDisabled,
  },
  teamSectionFormFieldChevron: {
    marginLeft: 4,
    alignSelf: 'center' as const,
  },
  /**
   * Native: fixed multiline block (Figma / `formTextAreaMinHeightNative` — mirrors
   * `projectDescriptionTextArea` in `ProjectInformation`).
   */
  teamNotesTextAreaNative: {
    minHeight: spacing.formTextAreaMinHeightNative,
    height: spacing.formTextAreaMinHeightNative,
  },
  selectLabel: {
    fontSize: 14,
    color: '#666',
    marginBottom: 8,
  },
  subsectionTitle: {
    fontSize: 16,
    fontWeight: '600',
    color: colors.primary,
    marginBottom: 20,
  },
  teamHelperText: {
    fontSize: fontSize.cardSubtitle,
    lineHeight: lineHeight.formControl,
    color: colors.textSecondary,
    marginBottom: spacing.fieldVerticalGap,
  },
  subsectionDescription: {
    fontSize: 14,
    color: '#666',
    marginBottom: 16,
    lineHeight: 20,
  },
  inlineValidationError: {
    color: '#dc3545',
    fontSize: 14,
    marginBottom: 12,
  },
  offlineWarning: {
    color: '#d97706',
    fontWeight: '600',
    fontStyle: 'italic',
  },
  teamSection: {
    marginBottom: 0,
    borderRadius: 8,
    overflow: 'hidden',
    borderWidth: 1,
    borderColor: '#e0e0e0',
    backgroundColor: '#fff',
    maxWidth: '100%' as const,
    alignSelf: 'stretch' as const,
  },
  teamHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 14,
    paddingHorizontal: 16,
    backgroundColor: '#f8f9fa',
    minWidth: 0,
  },
  teamTitle: {
    flexGrow: 1,
    flexShrink: 1,
    minWidth: 0,
    fontSize: 15,
    fontWeight: '500',
    color: '#333',
    marginRight: 12,
  },
  teamBadges: {
    flexDirection: 'row',
    alignItems: 'center',
    flexGrow: 0,
    flexShrink: 0,
  },
  teamBadge: {
    backgroundColor: '#28a745',
    paddingHorizontal: 10,
    paddingVertical: 4,
    borderRadius: 999,
    marginRight: 8,
  },
  teamBadgeText: {
    fontSize: 12,
    color: '#fff',
    fontWeight: '600',
  },
  teamBadgeEmpty: {
    backgroundColor: '#e9ecef',
    paddingHorizontal: 10,
    paddingVertical: 4,
    borderRadius: 999,
    marginRight: 8,
  },
  teamBadgeEmptyText: {
    fontSize: 12,
    color: '#6c757d',
    fontWeight: '500',
  },
  expandIcon: {
    marginLeft: 8,
    flexShrink: 0,
  },
  teamSectionDisabled: {
    opacity: 0.6,
    backgroundColor: '#f5f5f5',
  },
  teamTitleDisabled: {
    color: '#999',
  },
  teamBadgeDisabled: {
    backgroundColor: '#ccc',
  },
  selectedMembersContainer: {
    marginTop: 16,
    paddingTop: 16,
    borderTopWidth: 1,
    borderTopColor: '#e0e0e0',
  },
  memberList: {
    marginBottom: 16,
  },
  memberListTitle: {
    fontSize: 14,
    fontWeight: '600',
    color: '#333',
    marginBottom: 8,
  },
  defaultSponsorsMessage: {
    fontSize: 14,
    fontWeight: '500',
    color: '#666',
    marginBottom: 8,
    fontStyle: 'italic',
  },
  memberListItem: {
    paddingVertical: 8,
    paddingHorizontal: 12,
    backgroundColor: '#f8f9fa',
    borderRadius: 6,
    marginBottom: 6,
  },
  memberListInfo: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  memberListName: {
    fontSize: 14,
    fontWeight: '500',
    color: '#333',
    marginBottom: 2,
  },
  memberListEmail: {
    fontSize: 13,
    color: '#666',
  },
  accessLevelBadge: {
    backgroundColor: '#007bff',
    paddingHorizontal: 8,
    paddingVertical: 3,
    borderRadius: 999,
  },
  accessLevelBadgeText: {
    fontSize: 11,
    fontWeight: '600',
    color: '#fff',
  },
  defaultSponsorCheckboxContainer: {
    marginBottom: 12,
  },
  checkboxRow: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 8,
  },
  checkbox: {
    width: 13,
    height: 13,
    borderWidth: 2,
    borderColor: '#666',
    borderRadius: 3,
    marginRight: 8,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: '#fff',
  },
  checkboxChecked: {
    backgroundColor: '#007bff',
    borderColor: '#007bff',
  },
  checkmark: {
    color: '#fff',
    fontSize: 10,
    fontWeight: 'bold',
  },
  checkboxLabel: {
    fontSize: 13,
    color: '#333',
  },
  warningText: {
    fontSize: 13,
    color: '#ff6b00',
    marginTop: 4,
    fontStyle: 'italic',
  },
  infoText: {
    fontSize: 13,
    color: '#007bff',
    marginTop: 4,
    fontStyle: 'italic',
  },
});
